import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { UserPlus, Mail, Trash2, Building2, ShieldCheck, AlertCircle, Copy, Check } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

function StatusPill({ status, t }) {
  const map = {
    pending: { bg: 'bg-warning/10', fg: 'text-warning', label: t('staffInvite.statusPending') },
    accepted: { bg: 'bg-success/10', fg: 'text-success', label: t('staffInvite.statusAccepted') },
    revoked: { bg: 'bg-text-secondary/10', fg: 'text-text-secondary', label: t('staffInvite.statusRevoked') },
    expired: { bg: 'bg-danger/10', fg: 'text-danger', label: t('staffInvite.statusExpired') },
  };
  const s = map[status] || map.pending;
  return <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full ${s.bg} ${s.fg} font-medium`}>{s.label}</span>;
}

export default function TeamSection({ className }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', role: 'assistant', location_ids: [] });
  const [submitting, setSubmitting] = useState(false);
  const [lastAcceptUrl, setLastAcceptUrl] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  async function loadAll() {
    setLoading(true);
    try {
      const [tRes, iRes, lRes] = await Promise.all([
        api.get('/api/team').catch(() => ({ data: { users: [] } })),
        api.get('/api/staff-invites').catch(() => ({ data: { invites: [] } })),
        api.get('/api/locations').catch(() => ({ data: { locations: [] } })),
      ]);
      setTeam(tRes.data.users || []);
      setInvites(iRes.data.invites || []);
      setLocations(lRes.data.locations || []);
      // Default new-invite location to the active practice.
      const active = localStorage.getItem('studioops_active_practice');
      if (active && form.location_ids.length === 0) {
        setForm((f) => ({ ...f, location_ids: [active] }));
      }
    } finally { setLoading(false); }
  }

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'superadmin') loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  async function submitInvite(e) {
    e.preventDefault();
    if (!form.email || !form.first_name || !form.last_name) {
      toast.error(t('staffInvite.errors.requiredFields'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.location_ids || payload.location_ids.length === 0) delete payload.location_ids;
      const r = await api.post('/api/staff-invites', payload);
      toast.success(t('staffInvite.created'));
      setShowForm(false);
      setForm({ email: '', first_name: '', last_name: '', role: 'assistant', location_ids: [] });
      setLastAcceptUrl(r.data?.accept_url || null);
      loadAll();
    } catch (err) {
      const code = err.response?.data?.error;
      const msg =
        code === 'user_exists' ? t('staffInvite.errors.userExists') :
        code === 'forbidden_locations' ? t('staffInvite.errors.forbiddenLocations') :
        code === 'staff_seat_limit_reached' ? t('staffInvite.errors.seatLimit', { limit: err.response.data.limit }) :
        (err.response?.data?.message || t('staffInvite.errors.generic'));
      toast.error(msg);
    } finally { setSubmitting(false); }
  }

  async function revokeInvite(id) {
    if (!window.confirm(t('staffInvite.revokeConfirm'))) return;
    try { await api.delete(`/api/staff-invites/${id}`); toast.success(t('staffInvite.revoked')); loadAll(); }
    catch (e) { toast.error(e.response?.data?.message || t('staffInvite.errors.generic')); }
  }
  async function deactivateUser(id) {
    if (!window.confirm(t('team.deactivateConfirm'))) return;
    try { await api.delete(`/api/team/${id}`); toast.success(t('team.deactivated')); loadAll(); }
    catch (e) { toast.error(e.response?.data?.message || t('staffInvite.errors.generic')); }
  }
  async function toggleLocationOnUser(u, locId) {
    const current = u.location_ids || [u.practice_id];
    const next = current.includes(locId) ? current.filter((x) => x !== locId) : [...current, locId];
    if (next.length === 0) { toast.error(t('team.errorAtLeastOneLocation')); return; }
    try {
      await api.patch(`/api/team/${u._id}`, { location_ids: next });
      toast.success(t('team.locationsUpdated'));
      loadAll();
    } catch (e) { toast.error(e.response?.data?.message || t('staffInvite.errors.generic')); }
  }

  function toggleFormLocation(id) {
    setForm((f) => ({
      ...f,
      location_ids: f.location_ids.includes(id)
        ? f.location_ids.filter((x) => x !== id)
        : [...f.location_ids, id],
    }));
  }
  async function copyUrl(url) {
    try { await navigator.clipboard.writeText(url); setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 1800); } catch (_) {}
  }

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return null;

  return (
    <section className={`so-card p-6 ${className || ''}`} data-testid="team-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-base font-semibold text-text">{t('team.title')}</h2>
          <p className="text-xs text-text-secondary mt-1">{t('team.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="so-btn so-btn-primary text-xs"
          data-testid="team-invite-btn"
        >
          <UserPlus size={14}/> {showForm ? t('common.cancel') : t('team.inviteBtn')}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submitInvite} className="rounded-md border border-border bg-background/40 p-4 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="team-invite-form">
          <label className="text-sm">
            <span className="block text-xs font-medium text-text-secondary mb-1">{t('team.form.firstName')}</span>
            <input className="so-input" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="invite-first-name"/>
          </label>
          <label className="text-sm">
            <span className="block text-xs font-medium text-text-secondary mb-1">{t('team.form.lastName')}</span>
            <input className="so-input" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="invite-last-name"/>
          </label>
          <label className="text-sm md:col-span-2">
            <span className="block text-xs font-medium text-text-secondary mb-1">{t('team.form.email')}</span>
            <input type="email" className="so-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email"/>
          </label>
          <label className="text-sm">
            <span className="block text-xs font-medium text-text-secondary mb-1">{t('team.form.role')}</span>
            <select className="so-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role">
              <option value="assistant">{t('staffInvite.roleAssistant')}</option>
              <option value="admin">{t('staffInvite.roleAdmin')}</option>
            </select>
          </label>
          <div className="text-sm">
            <span className="block text-xs font-medium text-text-secondary mb-1">{t('team.form.locations')}</span>
            <div className="flex flex-wrap gap-2">
              {locations.map((l) => {
                const active = form.location_ids.includes(l._id);
                return (
                  <button
                    key={l._id}
                    type="button"
                    onClick={() => toggleFormLocation(l._id)}
                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${active ? 'bg-accent text-white border-accent' : 'bg-white text-text border-border hover:border-accent'}`}
                    data-testid={`invite-loc-${l.slug}`}
                  >
                    <Building2 size={11}/> {l.location_label || l.name}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-text-secondary mt-1">{t('team.form.locationsHint')}</p>
          </div>
          <div className="md:col-span-2 flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowForm(false)} className="so-btn so-btn-secondary text-xs">{t('common.cancel')}</button>
            <button type="submit" disabled={submitting} className="so-btn so-btn-primary text-xs" data-testid="invite-submit-btn">
              {submitting ? t('common.loading') : t('team.form.submit')}
            </button>
          </div>
        </form>
      )}

      {lastAcceptUrl && (
        <div className="rounded-md border border-accent/30 bg-accent/5 p-3 mb-5 flex items-start gap-2" data-testid="invite-accept-url-banner">
          <Mail size={14} className="text-accent mt-0.5 shrink-0"/>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-accent">{t('staffInvite.urlSent')}</p>
            <div className="flex items-center gap-2 mt-1">
              <input readOnly value={lastAcceptUrl} className="so-input font-mono text-[11px] flex-1 min-w-0" data-testid="invite-accept-url"/>
              <button type="button" onClick={() => copyUrl(lastAcceptUrl)} className="so-btn so-btn-secondary text-xs shrink-0">
                {copiedUrl ? <Check size={12}/> : <Copy size={12}/>}
                {copiedUrl ? t('settings.publicPage.copied') : t('settings.publicPage.copy')}
              </button>
            </div>
            <p className="text-[10px] text-text-secondary mt-1">{t('staffInvite.urlNote')}</p>
          </div>
        </div>
      )}

      {/* Active team members */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><ShieldCheck size={14}/> {t('team.activeTitle')}</h3>
        {loading ? <p className="text-xs text-text-secondary">{t('common.loading')}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="team-active-table">
              <thead className="text-left text-[11px] uppercase text-text-secondary border-b border-border">
                <tr>
                  <th className="py-2 pr-2">{t('team.table.name')}</th>
                  <th className="py-2 pr-2">{t('team.table.email')}</th>
                  <th className="py-2 pr-2">{t('team.table.role')}</th>
                  <th className="py-2 pr-2">{t('team.table.locations')}</th>
                  <th className="py-2 pr-2">{t('team.table.status')}</th>
                  <th className="py-2 pr-2 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {team.length === 0 && (
                  <tr><td colSpan={6} className="py-4 text-center text-text-secondary text-xs">{t('team.empty')}</td></tr>
                )}
                {team.map((m) => {
                  const userLocs = m.location_ids || [m.practice_id];
                  return (
                    <tr key={m._id} className="border-b border-border last:border-b-0" data-testid={`team-row-${m._id}`}>
                      <td className="py-2 pr-2">{m.first_name} {m.last_name}</td>
                      <td className="py-2 pr-2 text-text-secondary text-xs">{m.email}</td>
                      <td className="py-2 pr-2 text-xs">{m.role === 'admin' ? t('staffInvite.roleAdmin') : t('staffInvite.roleAssistant')}</td>
                      <td className="py-2 pr-2">
                        <div className="flex flex-wrap gap-1">
                          {locations.map((l) => {
                            const active = userLocs.includes(l._id);
                            const disabled = m._id === user._id; // Don't edit yourself
                            return (
                              <button
                                key={l._id}
                                onClick={() => !disabled && toggleLocationOnUser(m, l._id)}
                                disabled={disabled}
                                title={l.location_label || l.name}
                                className={`text-[10px] inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 ${active ? 'bg-accent text-white border-accent' : 'bg-white border-border text-text-secondary'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                data-testid={`team-loc-toggle-${m._id}-${l.slug}`}
                              >
                                <Building2 size={9}/>{l.location_label || l.name}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="py-2 pr-2 text-xs">
                        {m.active === false
                          ? <span className="text-danger">{t('team.statusInactive')}</span>
                          : <span className="text-success">{t('team.statusActive')}</span>}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        {m.active !== false && m._id !== user._id && (
                          <button onClick={() => deactivateUser(m._id)} className="text-danger hover:bg-danger/10 rounded p-1" title={t('team.deactivate')} data-testid={`team-deactivate-${m._id}`}>
                            <Trash2 size={14}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invitations */}
      <div>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Mail size={14}/> {t('staffInvite.invitationsTitle')}</h3>
        {invites.length === 0 ? (
          <p className="text-xs text-text-secondary flex items-center gap-1.5"><AlertCircle size={11}/> {t('staffInvite.noneYet')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="team-invites-table">
              <thead className="text-left text-[11px] uppercase text-text-secondary border-b border-border">
                <tr>
                  <th className="py-2 pr-2">{t('team.table.email')}</th>
                  <th className="py-2 pr-2">{t('team.table.role')}</th>
                  <th className="py-2 pr-2">{t('team.table.invitedBy')}</th>
                  <th className="py-2 pr-2">{t('team.table.expiresAt')}</th>
                  <th className="py-2 pr-2">{t('team.table.status')}</th>
                  <th className="py-2 pr-2 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((iv) => (
                  <tr key={iv._id} className="border-b border-border last:border-b-0" data-testid={`invite-row-${iv._id}`}>
                    <td className="py-2 pr-2">{iv.email}</td>
                    <td className="py-2 pr-2 text-xs">{iv.role === 'admin' ? t('staffInvite.roleAdmin') : t('staffInvite.roleAssistant')}</td>
                    <td className="py-2 pr-2 text-xs text-text-secondary">{iv.invited_by_name}</td>
                    <td className="py-2 pr-2 text-xs text-text-secondary">{new Date(iv.expires_at).toLocaleDateString('de-AT')}</td>
                    <td className="py-2 pr-2"><StatusPill status={iv.status} t={t}/></td>
                    <td className="py-2 pr-2 text-right">
                      {iv.status === 'pending' && (
                        <button onClick={() => revokeInvite(iv._id)} className="text-danger hover:bg-danger/10 rounded p-1" title={t('staffInvite.revoke')} data-testid={`invite-revoke-${iv._id}`}>
                          <Trash2 size={14}/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
